// ============================================================
// MarkDeliveredModal.tsx — B3: "Mark as sent by post / handed over".
// Records a non-email delivery of an ISSUED bill (POST /api/invoices/:id/
// delivered): method POST or HAND, and the date it was posted / handed over
// (not in the future, not before the bill was issued — the server re-checks).
// The first delivery sets the bill's delivery date; a later one is added to
// the bill history only. Needs approved bill wording (server-enforced).
// B3b — the same for an issued CREDIT NOTE (creditNote prop: POST
// /api/credit-notes/:id/delivered). The date error uses FieldError.
// B3c — the date is Casevo's DateInput (DD / MM / YYYY), not the browser's
// date box; a half-typed date holds Save.
// ============================================================
import React, { useState } from 'react'
import ModalFrame from '../shared/ModalFrame'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import DateInput from '../shared/DateInput'
import { markBillDeliveredApi, markCreditNoteDeliveredApi } from '../../api/casevoApi'
import type { Bill, CreditNote } from '../../types/billing.types'
import { BIcon, B_PRIMARY, B_SECONDARY, LABEL, NOTE, errorText } from './billingUi'
import { isRealIsoDate, londonDayOf, londonToday, ukDate } from '../../utils/civilDate'

export type ManualDeliveryMethod = 'POST' | 'HAND'

// Pure: why the date can't be saved (null = fine). Exported for tests.
export const deliveryDateError = (date: string | null, today: string, issuedDay: string | null, what: 'bill' | 'credit note' = 'bill'): string | null => {
  if (!date || !isRealIsoDate(date)) return 'Enter the date.'
  if (date > today) return 'The date can’t be in the future.'
  if (issuedDay && date < issuedDay) return `The date can’t be before the ${what} was issued (${ukDate(issuedDay)}).`
  return null
}

const MarkDeliveredModal: React.FC<{ bill: Bill; creditNote?: CreditNote; onClose: () => void; onDone: (b: Bill) => void }> = ({ bill, creditNote, onClose, onDone }) => {
  const today = londonToday()
  const doc = creditNote ? { what: 'credit note' as const, number: creditNote.number, issuedAt: creditNote.issuedAt, delivery: creditNote.delivery ?? null }
    : { what: 'bill' as const, number: bill.number, issuedAt: bill.issuedAt, delivery: bill.delivery }
  const issuedDay = doc.issuedAt ? londonDayOf(new Date(doc.issuedAt)) : null
  const [method, setMethod] = useState<ManualDeliveryMethod>('POST')
  const [date, setDate] = useState<string | null>(today)
  const [dateTyping, setDateTyping] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const dateError = deliveryDateError(date, today, issuedDay, doc.what)

  const save = async (): Promise<void> => {
    if (dateError || dateTyping || !date || busy) return
    setBusy(true); setErr(null)
    try { onDone(await (creditNote ? markCreditNoteDeliveredApi(creditNote.id, { method, date }) : markBillDeliveredApi(bill.id, { method, date }))) }
    catch (e) { setErr(errorText(e, 'Couldn’t record the delivery.')) } finally { setBusy(false) }
  }

  const opt = (m: ManualDeliveryMethod, label: string, sub: string) => (
    <label className={`flex gap-[10px] items-start rounded-[10px] border px-3 py-[9px] cursor-pointer ${method === m ? 'border-casevo-brand bg-casevo-mint/50' : 'border-casevo-border'}`}>
      <input type="radio" name="delivery-method" className="mt-[3px] accent-casevo-brand" checked={method === m} onChange={() => setMethod(m)} />
      <span><span className="block text-[13.5px] font-semibold text-casevo-forest">{label}</span><span className={NOTE}>{sub}</span></span>
    </label>
  )

  return (
    <ModalFrame onClose={() => { if (!busy) onClose() }} dismissible={!busy} size="sm" portal eyebrow="Record delivery" eyebrowIcon={<BIcon name="send" size={14} />}
      title={`How was ${doc.number} delivered?`}
      subtitle={doc.delivery ? `First delivered on ${ukDate(londonDayOf(new Date(doc.delivery.at)))}. This adds to the bill history; the delivery date stays.` : `The date you give becomes the ${doc.what}’s delivery date.`}
      footerRight={<>
        <button type="button" className={B_SECONDARY} disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className={B_PRIMARY} disabled={!!dateError || dateTyping || busy} onClick={() => { void save() }}>{busy ? 'Saving…' : 'Record delivery'}</button>
      </>}>
      <div className="grid gap-4">
        <fieldset className="grid gap-2">
          <legend className={LABEL}>Method</legend>
          {opt('POST', 'Sent by post', 'Posted to the client’s address')}
          {opt('HAND', 'Handed over', 'Given to the client in person')}
        </fieldset>
        <FormField label={method === 'POST' ? 'Date posted' : 'Date handed over'} required variant="info">
          <DateInput value={date} onChange={setDate} ariaLabel={method === 'POST' ? 'Date posted' : 'Date handed over'} onValidityChange={(ok) => setDateTyping(!ok)} />
          {!dateTyping && <span id="md-date-err"><FieldError message={dateError ?? undefined} /></span>}
        </FormField>
        {err && <p role="alert" className="m-0 text-[13px] text-casevo-danger">{err}</p>}
      </div>
    </ModalFrame>
  )
}

export default MarkDeliveredModal
